/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'

import { View } from '@components/atoms'
import { mono, monoFont, monoShared } from '@components/systems/monolith/monolith.tokens'

/**
 * `<body>` 스크롤 잠금 클래스. 규칙은 `index.css`(쿠키 모달과 같은 규칙, 이름만 다르다).
 * 쿠키 모달의 `mnCookieLock` 을 그대로 쓰지 않는 이유: 프리렌더 스크립트가 그 이름을 걷어낸다.
 */
const BODY_LOCK_CLASS = 'mnModalLock'

interface PropsType {
	isOpen: boolean
	title: string
	body: string
	confirmLabel: string
	cancelLabel: string
	onConfirm: () => void
	onCancel: () => void
}

/**
 * 확인 모달 — Monolith(2안). 쿠키 동의 모달(§19)과 같은 문법이다: 잉크 70% 스크림,
 * 640 이하 surface 패널, radius 0, 헤어라인, Tab 순환, 스크롤 잠금(iOS 포함).
 *
 * 쿠키 모달과 다른 점 둘. ① 동의가 아니라 **되돌릴 수 있는 확인**이라 Escape·스크림 클릭이
 * 취소다. ② 선택지가 대등할 필요가 없어서 주 동작은 블랙 CTA, 취소는 텍스트 링크 —
 * 페이지의 "주 CTA 하나" 규칙(DESIGN_SYSTEM §5) 그대로다.
 *
 * 문구는 호출하는 쪽이 준다. 이 컴포넌트는 묻지 않는다 — 단정문으로 무슨 일이 일어나는지 적고
 * 판단은 읽는 쪽에 맡긴다(PERSONA §1).
 */
export const MonolithConfirmModal = (props: PropsType) => {
	const { isOpen, title, body, confirmLabel, cancelLabel, onConfirm, onCancel } = props

	const panelRef = React.useRef<HTMLDivElement>(null)
	const lastFocusedRef = React.useRef<Element | null>(null)

	React.useEffect(() => {
		if (!isOpen) return

		const scrollY = window.scrollY

		lastFocusedRef.current = document.activeElement
		document.body.classList.add(BODY_LOCK_CLASS)
		document.body.style.top = `-${scrollY}px`
		panelRef.current?.focus()

		return () => {
			document.body.classList.remove(BODY_LOCK_CLASS)
			document.body.style.top = ''
			window.scrollTo(0, scrollY)

			const last = lastFocusedRef.current
			if (last instanceof HTMLElement) {
				last.focus()
			}
		}
	}, [isOpen])

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault()
			onCancel()
			return
		}
		if (e.key !== 'Tab') return

		const panel = panelRef.current
		if (panel === null) return

		const focusable = panel.querySelectorAll<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		)
		if (focusable.length === 0) return

		const first = focusable[0]
		const last = focusable[focusable.length - 1]
		const active = document.activeElement

		if (e.shiftKey && (active === first || active === panel)) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && active === last) {
			e.preventDefault()
			first.focus()
		}
	}

	if (!isOpen) return null

	return (
		<View
			css={style}
			role="dialog"
			aria-modal="true"
			aria-labelledby="mnConfirmTitle"
			aria-describedby="mnConfirmBody"
			onKeyDown={onKeyDown}
			onClick={onCancel}
		>
			<View
				className="mnConfirmPanel"
				ref={panelRef}
				tabIndex={-1}
				onClick={e => e.stopPropagation()}
			>
				<h2 className="mnConfirmTitle" id="mnConfirmTitle">
					{title}
				</h2>
				<p className="mnConfirmBody" id="mnConfirmBody">
					{body}
				</p>

				<View className="mnConfirmActions">
					<button type="button" className="mnCta" onClick={onConfirm}>
						{confirmLabel}
					</button>
					<button type="button" className="mnTextLink" onClick={onCancel}>
						{cancelLabel}
					</button>
				</View>
			</View>
		</View>
	)
}

const style = css([
	monoShared,
	{
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 1400,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 20,
		background: 'rgba(22, 24, 27, 0.7)',
		fontFamily: monoFont.sans,
		boxSizing: 'border-box',

		'.mnConfirmPanel': {
			width: '100%',
			maxWidth: 520,
			maxHeight: 'calc(100vh - 40px)',
			overflowY: 'auto',
			padding: 'clamp(32px, 4vw, 48px)',
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			borderRadius: 0,
			boxSizing: 'border-box',
			textAlign: 'left',
			outline: 'none',

			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 3,
			},
		},

		'.mnConfirmTitle': {
			margin: 0,
			fontSize: 'clamp(24px, 2.4vw, 30px)',
			lineHeight: 1.25,
			fontWeight: 200,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},

		'.mnConfirmBody': {
			margin: '18px 0 0',
			fontSize: 16,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
		},

		'.mnConfirmActions': {
			display: 'flex',
			alignItems: 'center',
			gap: 28,
			flexWrap: 'wrap',
			marginTop: 32,
		},
	},
])
